import { RecordingSaved, TimeLimitText } from '@carboncopyco/components';
import {
  type CreateDemoArgs,
  useCreateDemoMutation,
  useFinishRecordingMutation,
  useRecorderNewMutation,
  useSaveRecordingPageMutation,
} from '@carboncopyco/data';
import { type IRecording, RecordingTypes } from '@carboncopyco/types';
import { type IGlobalTab, TabType } from '@carboncopyco/types/desktop';
import { Button, notify } from '@carboncopyco/ui';
import {
  AnalyticsTrackEventName,
  formatPlural,
  useAnalytics,
} from '@carboncopyco/utils';
import { useState } from 'react';
import type {
  IUserClickPayload,
  IUserVisitPayload,
} from '../../main/scripts/trackUserActivity';
import { AnalyticsEvents } from '../../shared/constants/analytics.events';
import { CLICK_KEY, VISIT_KEY } from '../../shared/constants/constants';
import { KnowledgeBaseLinksEnum } from '../../shared/enums/KnowledgeBaseLinksEnum';
import { UserEventsEnum } from '../../shared/enums/UserEventsEnum';
import analyticsApi, {
  type ISessionData,
} from '../../shared/services/analytics-api';
import ModalWrapper from '../components/Modals/ModalWrapper';
import StartRecording from '../components/StartRecording';
import { useTextExtraction } from '../components/TextExtraction';
import { useCloneScreenRecording } from '../hooks/useCloneScreenRecording';
import { useFlagsFallback } from '../hooks/useFlagsFallback';
import { useSystemPermissions } from '../hooks/useSystemPermissions';
import ScreenPermissionModal from '../modals/ScreenPermissionModal';
import { useTabManager } from '../services/TabManager';
import { useTabs, useUser } from '../store/context';
import { useOpenDemoEditorTab } from './utils/openDemoEditorTab';
import { useClonePreview } from './utils/useClonePreview';

const listenToUserEvents = (sessionData: ISessionData) => {
  window.dsDesktop.ipcRenderer.on(
    UserEventsEnum.MAIN_TO_RENDERER_USER_ACTIVITY,
    (activityType, data) => {
      // eslint-disable-next-line no-console
      console.debug(
        `received user activity ${activityType}: ${JSON.stringify(data)}`
      );
      switch (activityType) {
        case CLICK_KEY: {
          const clickPayload: IUserClickPayload = data as IUserClickPayload;
          analyticsApi.eventV2.click({
            ...sessionData,
            ...clickPayload,
            resource_type: 'record',
          });
          break;
        }
        case VISIT_KEY: {
          const visitPayload: IUserVisitPayload = data as IUserVisitPayload;
          analyticsApi.eventV2.visit({
            ...sessionData,
            ...visitPayload,
          });
          break;
        }
        default:
          // eslint-disable-next-line no-console
          console.error(`user activity type of ${activityType} is not valid`);
      }
    }
  );
};

export const startTrackingUserActivity = (
  recording: IRecording,
  organizationId: number
) => {
  // eslint-disable-next-line no-console
  console.debug('starting user activity session');
  const sessionData: ISessionData = {
    user: {
      name: recording.creator.name,
      email: recording.creator.email,
      role: recording.creator.role,
    },
    session_id: recording.recorderUuid || '',
    organization_id: organizationId,
    record_id: recording.id,
    revision_id: recording.revisionId,
  };
  analyticsApi.eventV2.startSession(sessionData);
  listenToUserEvents(sessionData);
};

export const stopTrackingUserActivity = (recorderSessionId?: string) => {
  // eslint-disable-next-line no-console
  console.debug('ending user activity session');
  analyticsApi.eventV2.endSession(recorderSessionId);
  window.dsDesktop.ipcRenderer.removeAllListeners(
    UserEventsEnum.MAIN_TO_RENDERER_USER_ACTIVITY
  );
};

export const StartRecordingScreen = ({
  globalTab,
}: {
  globalTab: IGlobalTab;
}) => {
  const [pageTitle, setPageTitle] = useState<string>('');
  const [isRecording, setIsRecording] = useState(false);
  const [isStopModalOpened, setIsStopModalOpened] = useState(false);
  const [isLoadingRecording, setIsLoadingRecording] = useState(false);
  const [newCloneId, setNewCloneId] = useState<number | string | undefined>();
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] =
    useState<boolean>(false);
  const openClonePreview = useClonePreview();
  const { trackEvent } = useAnalytics();
  const { platformUpdateCloneTimeLimit: updateCloneTimeLimit } =
    useFlagsFallback();
  const openDemoEditorTab = useOpenDemoEditorTab();
  const [finishRecording] = useFinishRecordingMutation();
  const { activeOrganization } = useUser();
  const { activeGlobalTabId, activeRecordingData, setActiveRecordingData } =
    useTabs();
  const [recorderNew] = useRecorderNewMutation();
  const [createDemo] = useCreateDemoMutation();
  const [saveRecordingPage] = useSaveRecordingPageMutation();

  const {
    hasScreenRecordingPermission,
    alreadyAskedScreenRecording,
    storePermissionRequest,
  } = useSystemPermissions();
  const { startScreenRecording, stopScreenRecording } =
    useCloneScreenRecording();

  const {
    closeGlobalTab,
    closeGlobalActiveTab,
    getActiveGlobalTab,
    createGlobalTab,
    updateGlobalTabPayload,
  } = useTabManager();

  const { startTextExtraction, stopTextExtraction } = useTextExtraction({
    recording: activeRecordingData,
    tabId: globalTab?.id,
  });

  const getTimeLimit = (value: number) => {
    if (value >= 1) {
      return `${value} ${formatPlural('day', value)}`;
    }
    return `${value * 24} ${formatPlural('hour', value * 24)}`;
  };

  const onSavedRecordingSubmitted = async (data: any) => {
    setIsLoadingRecording(true);
    trackEvent(AnalyticsTrackEventName.CLONE_CLICK_SAVE_CLONING, {
      location: 'recording_page',
    });
    const limitStringLength = 300;
    try {
      const id = activeRecordingData?.id || 0;
      const finishRecordingResponse = await finishRecording({
        organizationId: `${activeOrganization?.id}`,
        recordingId: Number(id),
        recordName:
          data?.recordName?.substring(0, limitStringLength) ||
          data?.cloneName?.substring(0, limitStringLength),
        description: data.description.substring(0, limitStringLength),
        workspaceId: data.workspaceId,
        folderId: globalTab.folderId,
      }).unwrap();
      window.dsDesktop.recorder.updateRecordingTab({
        ...finishRecordingResponse,
        globalTab,
      });
      await stopScreenRecording(activeGlobalTabId, finishRecordingResponse);
      await window.dsDesktop.recorder.destroyRecorder();
      setNewCloneId(finishRecordingResponse?.id);
      setPageTitle(finishRecordingResponse?.recordName);
      setIsLoadingRecording(false);
      setIsRecording(false);
      updateGlobalTabPayload(getActiveGlobalTab()?.id, {
        cloningInProgress: false,
      });
      setIsStopModalOpened(true);

      stopTextExtraction();
    } catch (e) {
      setIsLoadingRecording(false);
      console.error('Finish recording error', e);
      notify.error('Something went wrong please try again');
    }
    stopTrackingUserActivity(activeRecordingData?.recorderUuid);
  };

  const startCloning = async () => {
    setIsLoadingRecording(true);
    trackEvent(AnalyticsTrackEventName.CLONE_CLICK_START_CLONING, {
      location: 'recording_page',
    });
    await window.dsDesktop.recorder.onStartRecording(getActiveGlobalTab());

    const { activeInnerTabId } = getActiveGlobalTab();
    if (!activeInnerTabId) {
      // eslint-disable-next-line no-console
      console.error('Inner tab id is missing');
      return;
    }
    const { title, url } =
      await window.dsDesktop.tabs.getInnerTabData(activeInnerTabId);
    const encodeUrl = encodeURIComponent(url);
    const newRecording = await recorderNew({
      organizationId: `${activeOrganization.id}`,
      recorderUuid: activeRecordingData?.recorderUuid || '',
      firstUrl: encodeUrl,
      recordName: title || new Date().toString(),
      description: 'description',
      recordingType: RecordingTypes.native,
    }).unwrap();
    saveRecordingPage({
      organizationId: `${activeOrganization.id}`,
      recordingId: `${newRecording.id}`,
      url: encodeUrl,
    });
    window.dsDesktop.recorder.updateRecordingTabStarted(newRecording);
    setActiveRecordingData({
      ...activeRecordingData,
      ...newRecording,
    });
    setIsLoadingRecording(false);
    updateGlobalTabPayload(getActiveGlobalTab()?.id, {
      cloningInProgress: true,
    });
    setIsRecording(true);
    startTrackingUserActivity(newRecording, activeOrganization.id);

    startTextExtraction();
  };

  const handleOnStartRecording = async () => {
    const hasAskedForScreenRecordingPermission =
      await alreadyAskedScreenRecording();
    const shouldShowModal =
      !hasScreenRecordingPermission && !hasAskedForScreenRecordingPermission;

    if (shouldShowModal) {
      setIsPermissionsModalOpen(true);
      return;
    }

    await startRecording(hasScreenRecordingPermission);
    return;
  };

  const startRecording = async (permissionGranted = false) => {
    if (!activeRecordingData) return;

    if (permissionGranted) {
      try {
        await startScreenRecording(activeGlobalTabId, activeRecordingData);
      } catch (error) {
        if (error instanceof Error && error.name === 'NotReadableError') {
          return;
        }
        console.error('Error starting screen recording', error);
      }
    }

    await startCloning();
  };

  const onExitRecorder = () => {
    trackEvent(AnalyticsTrackEventName.CLONE_CLICK_DISCARD, {
      location: 'recording_page',
    });
    window.dsDesktop.recorder.exitRecorder(getActiveGlobalTab());
    closeGlobalTab(getActiveGlobalTab()?.id);
  };

  const handlePreviewSavedRecording = async () => {
    trackEvent(AnalyticsTrackEventName.CLONE_CLICK_PREVIEW, {
      location: 'cloning_saved_modal',
    });
    closeGlobalActiveTab();
    window.dsDesktop.recorder.exitRecorder(globalTab);

    openClonePreview({
      cloneId: (newCloneId || '').toString(),
      createGlobalTab,
      activeOrganization: {
        id: `${activeOrganization?.id}`,
      },
    });
  };

  const handleLearnMore = () => {
    window.dsDesktop?.shellOpenExternal(
      KnowledgeBaseLinksEnum.ContinueCloningPage
    );
  };

  const timeLimit = getTimeLimit(updateCloneTimeLimit);

  const onPermissionClick = async (hasGrantedPermission: boolean) => {
    await storePermissionRequest();

    if (!hasGrantedPermission) {
      setIsPermissionsModalOpen(false);
      await startRecording(false);
      return;
    }

    setIsPermissionsModalOpen(false);
    await startRecording(true);
    return;
  };

  return (
    <>
      <StartRecording
        updateRecording={false}
        isRecording={isRecording}
        isLoading={isLoadingRecording}
        onStartRecording={handleOnStartRecording}
        onSavedRecordingSubmitted={onSavedRecordingSubmitted}
        onExitRecorder={onExitRecorder}
      />
      <ModalWrapper
        isVisible={isStopModalOpened}
        onCloseModal={() => setIsStopModalOpened(false)}
        isCenterPosition
        noPadding
        locked
      >
        <RecordingSaved
          onDismiss={() => {
            closeGlobalActiveTab();
            window.dsDesktop.recorder.exitRecorder(globalTab);
          }}
          previewSavedRecording={() => {
            handlePreviewSavedRecording();
          }}
          handleRedirectToEditor={async () => {
            trackEvent(AnalyticsTrackEventName.CLONE_CLICK_OPEN_IN_EDITOR, {
              location: 'cloning_saved_modal',
            });

            closeGlobalActiveTab();
            window.dsDesktop.recorder.exitRecorder(globalTab);
            if (!newCloneId) return;
            const { folderId } = globalTab;

            const createDemoArgs: CreateDemoArgs = {
              recordingId: newCloneId,
              organizationId: activeOrganization.id?.toString(),
              demoName: pageTitle,
              ...(folderId && folderId !== '0' ? { folderId } : {}),
            };
            try {
              const demo = await createDemo(createDemoArgs).unwrap();
              await trackEvent(AnalyticsEvents.CLONE_OPEN_IN_PREVIEW, {
                location: 'recording_saved_modal',
                recordingId: newCloneId,
                demoId: demo.id,
              });
              openDemoEditorTab(demo, Number(activeOrganization.id));
            } catch (_e) {
              notify.error('Cannot create demo');
            }
          }}
          handleRedirectToDashboard={() => {
            trackEvent(AnalyticsTrackEventName.CLONE_CLICK_CREATE_NEW, {
              location: 'cloning_saved_modal',
            });
            closeGlobalActiveTab();
            window.dsDesktop.recorder.exitRecorder(getActiveGlobalTab());
            createGlobalTab({ type: TabType.CLONE });
          }}
          timeLimitText={
            !!updateCloneTimeLimit && (
              <TimeLimitText
                timeLimit={timeLimit}
                learnMore={
                  <Button
                    className="text-sm font-ds-inter font-ds-medium text-ds-primary"
                    onClick={handleLearnMore}
                  >
                    Learn more
                  </Button>
                }
              />
            )
          }
        />
      </ModalWrapper>
      <ModalWrapper isVisible={isPermissionsModalOpen}>
        <ScreenPermissionModal
          onAccept={() => onPermissionClick(true)}
          onDecline={() => onPermissionClick(false)}
          onClose={() => setIsPermissionsModalOpen(false)}
        />
      </ModalWrapper>
    </>
  );
};
